import { useEffect, useState } from "react";
import useIsMobile from "../../../../hooks/useIsMobile";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { AnimatePresence, motion } from "framer-motion";
import { Skeleton } from "primereact/skeleton";

const RemainingPackageList = (props) => {
  const { data, filterValue, loading } = props;
  const [viewType, setViewType] = useState("card");
  const viewOptions = [
    { label: "Card View", value: "card" },
    { label: "Table View", value: "table" },
  ];
  const [selectedProduct, setSelectedProduct] = useState(null);
  const isMobile = useIsMobile(750);

  useEffect(() => {
    if (isMobile) {
      setViewType("card");
    } else {
      setViewType("table");
    }
  }, [isMobile]);

  const formatPax = (value) => `${value} pax`;

  const onRowSelect = (event) => {
    window.location.href = `/report/remaining-customer-packages-detail/${event.data.product_id}/${event.data.product_name}`;
  };

  // Body templates for card and table views
  const mobileBodyTemplate = (rowData, event) => {
    const { rowIndex } = event;
    const no = rowIndex + 1;

    return (
      <div className="col-center-start gap-2 w-full">
        <div className="row-start-start gap-2 w-full">
          <span className="flex items-center justify-center text-sm font-bold text-purple-500">
            {no}.
          </span>
          <span className="w-full font-semibold text-black text-sm truncate">
            {rowData.product_name}
          </span>
        </div>
        <div className="w-full font-semibold text-sm">
          {rowData.type.toLowerCase() === "free product" ? (
            <span className="rounded-md bg-teal-600 text-white px-2 py-0.5">
              Free Product
            </span>
          ) : (
            "Package"
          )}
        </div>
        <span className="text-sm text-gray-700 font-semibold">
          Quantity: {formatPax(rowData.total)}
        </span>
        <span className="text-sm text-gray-700 font-semibold">
          Price: Rp {Intl.NumberFormat('id-ID').format(rowData.product_price_v1)}
        </span>
        <span className="text-sm text-gray-700 font-semibold">
          Total: Rp {Intl.NumberFormat('id-ID').format(rowData.total_price_v1)}
        </span>
      </div>
    );
  };

  const nameBodyTemplate = (rowData, event) => {
    const { rowIndex } = event;
    const no = rowIndex + 1;

    return (
      <div className="row-start-center gap-2">
        <span className="min-w-[3rem] min-h-[3rem] w-[3rem] h-[3rem] flex items-center justify-center text-md font-bold text-purple-500">
          {no}.
        </span>
        <span className="w-full font-semibold text-sm">
          {rowData.product_name}
        </span>
      </div>
    );
  };

  const typeBodyTemplate = (rowData) => {
    return (
      <div className="w-full font-semibold text-sm">
        {rowData.type.toLowerCase() === "free product" ? (
          <span className="inline-block rounded-full bg-teal-700 text-white px-3 py-0.5 text-xs text-center">
            Free Product
          </span>
        ) : (
          <span className="inline-block rounded-full bg-gray-200 text-gray-900 px-3 py-0.5 text-xs text-center">
            Package
          </span>
        )}
      </div>
    );
  };

  const packageBodyTemplate = (rowData) => {
    return (
      <div className="w-full font-semibold text-sm">
        {formatPax(rowData.total)}
      </div>
    );
  };

  const priceBodyTemplate = (rowData) => {
    return (
      <div className="w-full font-semibold text-sm">
        Rp {Intl.NumberFormat('id-ID').format(rowData.product_price_v1)}
      </div>
    );
  };

  const totalPriceBodyTemplate = (rowData) => {
    return (
      <div className="w-full font-semibold text-sm">
        Rp {Intl.NumberFormat('id-ID').format(rowData.total_price_v1)}
      </div>
    );
  }

  // Skeleton for table rows
  const TableSkeleton = ({ rows = 10 }) => {
    const skeletonRows = Array.from({ length: rows }, (_, i) => ({ id: i }));

    const mobileSkeleton = () => (
      <div className="col-center-start gap-2 w-full">
        <div className="row-start-start gap-2 w-full">
          <Skeleton
            width="1.25rem"
            height="1.25rem"
            className="flex-shrink-0"
          />
          <Skeleton width="10rem" height="1rem" />
        </div>
        <Skeleton width="7rem" height="1rem" />
        <Skeleton width="10rem" height="1rem" />
      </div>
    );

    const nameSkeleton = () => (
      <div className="row-start-center gap-2">
        <Skeleton width="2rem" height="2rem" className="flex-shrink-0" />
        <Skeleton width="70%" height="1rem" />
      </div>
    );

    const typeSkeleton = () => <Skeleton width="6rem" height="1rem" />;

    const packageSkeleton = () => <Skeleton width="5rem" height="1rem" />;

    return (
      <div className="w-full border border-gray-300 rounded-xl overflow-hidden">
        <DataTable value={skeletonRows} className="w-full" stripedRows>
          <Column
            header="Product Name"
            headerStyle={viewType === "card" ? { display: "none" } : undefined}
            body={viewType === "card" ? mobileSkeleton : nameSkeleton}
            style={viewType === "table" ? { width: "50%" } : {}}
          />
          {viewType === "card" ? null : (
            <Column header="Product Type" body={typeSkeleton} />
          )}
          {viewType === "card" ? null : (
            <Column header="Total" body={packageSkeleton} />
          )}
        </DataTable>
      </div>
    );
  };

  return (
    <div className="elevated-card-tight-vertical w-full !shadow-md col-center-start gap-4 border border-gray-300 sm:!px-6">
      <div className="w-full flex flex-col sm:flex-row gap-2 justify-between items-start sm:items-center sm:justify-between">
        <div className="flex flex-col items-start gap-1">
          <label className="flex items-center gap-2 text-lg font-semibold text-black">
            <i className="pi pi-cart-arrow-down text-green-500 text-lg"></i>
            Remaining Packages
          </label>
        </div>

        <div className="flex gap-2 rounded-md p-1 w-full sm:w-auto">
          {viewOptions.map((option) => (
            <button
              key={option.label}
              onClick={() => setViewType(option.value)}
              className={`px-4 py-1.5 w-full sm:w-auto rounded-lg text-sm sm:text-base font-semibold transition-all duration-300 ease-in-out 
                ${
                  viewType === option.value
                    ? "bg-gradient-to-br from-green-400 to-emerald-500 text-white shadow"
                    : "text-black hover:bg-green-100 bg-slate-100"
                }`}
              disabled={loading}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="col-center-start gap-2">
        <div className="w-full row-start-center gap-2">
          <i className="pi pi-box text-lg sm:text-lg text-green-500"></i>

          <AnimatePresence mode="wait">
            <span className="flex flex-row justify-start items-center gap-2 text-md sm:text-lg font-semibold text-green-500 overflow-wrap">
              {loading ? (
                <motion.div
                  key="skeleton"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Skeleton width="10rem" height="1.75rem" />
                </motion.div>
              ) : (
                <motion.div
                  key="data"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  Total Packages : {formatPax(data?.total_packages) || "0"}
                </motion.div>
              )}
            </span>
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full"
          >
            <TableSkeleton rows={10} />
          </motion.div>
        ) : viewType === "card" ? (
          <motion.div
            key="card"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full border border-gray-300 rounded-xl overflow-hidden"
          >
            <DataTable
              value={data?.packages}
              className="w-full"
              stripedRows
              selectionMode="single"
              onRowSelect={onRowSelect}
              selection={selectedProduct}
              onSelectionChange={(e) => setSelectedProduct(e.value)}
              rowClassName={() =>
                "transition-transform duration-300 hover:-translate-y-1 rounded-md hover:shadow-md"
              }
              paginator
              rows={10}
              rowsPerPageOptions={[10, 20, 30, 50, 100, 500]}
              paginatorTemplate={{
                layout: isMobile
                  ? "PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
                  : "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown",
              }}
            >
              <Column
                field="mobileProduct"
                header="Product Name"
                body={mobileBodyTemplate}
                headerStyle={{ display: "none" }}
              />
            </DataTable>
          </motion.div>
        ) : (
          <motion.div
            key="table"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full border border-gray-300 rounded-xl overflow-hidden"
          >
            <DataTable
              value={data?.packages}
              className="w-full"
              stripedRows
              selectionMode="single"
              onRowSelect={onRowSelect}
              selection={selectedProduct}
              onSelectionChange={(e) => setSelectedProduct(e.value)}
              rowClassName={() =>
                "transition-transform duration-300 hover:-translate-y-1 rounded-md hover:shadow-md"
              }
              removableSort
              sortMode="multiple"
              paginator
              rows={10}
              rowsPerPageOptions={[10, 20, 30, 50, 100, 500]}
              paginatorTemplate={{
                layout: isMobile
                  ? "PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
                  : "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown",
              }}
            >
              <Column
                field="product_name"
                header="Product Name"
                body={nameBodyTemplate}
                style={{ width: "50%" }}
              />
              <Column
                field="type"
                header="Product Type"
                body={typeBodyTemplate}
                sortable
              />
              <Column
                field="total"
                header="Quantity"
                body={packageBodyTemplate}
                sortable
              />
              <Column
                field="product_price_v1"
                header="@"
                body={priceBodyTemplate}
                sortable
              />
              <Column
                field="total_price_v1"
                header="Total"
                body={totalPriceBodyTemplate}
                sortable
              />
            </DataTable>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RemainingPackageList;
